import { useState } from 'react';
import { Eye, FileText, Lock, Pencil, Upload } from 'lucide-react';
import type { DocumentType } from '@kmdx/contracts';
import { cn } from '@/lib/cn';
import { daysUntil, formatDate } from '@/lib/format';
import { useDocumentMeta, useDocumentsOf } from '@/api/hooks/documents';
import { useUploadDocument } from '@/api/hooks/uploads';
import type { DocumentSummary } from '@/api/types';
import { Badge } from '@/components/ui/Badge';
import { Button, IconButton } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { DatePicker, Field, Input } from '@/components/ui/Field';
import { LoadingBlock } from '@/components/ui/State';
import { toast } from '@/stores/toast';

interface Slot {
  type: DocumentType;
  label: string;
  hint: string;
  /** Има ли срок, който да се следи. */
  expires: boolean;
  /** Предложение за срока при качване — в месеци от днес. */
  suggestMonths?: number;
}

/** Клиентът: „лична карта, шофьорска, медицинско, свидетелство за съдимост, банкова сметка, сертификати, Macron". */
const SLOTS: Slot[] = [
  { type: 'id_card', label: 'Лична карта', hint: 'Лицето и гърба — две снимки.', expires: true },
  { type: 'driving_licence', label: 'Шофьорска книжка', hint: 'Лицето и гърба. Срокът отива и в картона на шофьора.', expires: true },
  { type: 'medical_certificate', label: 'Медицинско', hint: 'Срокът отива и в картона на шофьора.', expires: true },
  { type: 'criminal_record', label: 'Свидетелство за съдимост', hint: 'Обикновено важи 6 месеца от издаването.', expires: true, suggestMonths: 6 },
  { type: 'bank_details', label: 'Банкова сметка', hint: 'Удостоверение от банката или снимка на картата с IBAN.', expires: false },
  { type: 'certificate', label: 'Сертификати', hint: 'ADR, курсове, квалификации — по един файл на сертификат.', expires: true },
  { type: 'posting_certificate', label: 'Macron (командироване)', hint: 'Удостоверението за командироване във Франция.', expires: true },
];
const TYPES = SLOTS.map((s) => s.type).join(',');

/**
 * ЛИЧНИТЕ ДОКУМЕНТИ НА ШОФЬОРА — само за управителя (решение на клиента).
 * Сървърът не ги дава на никой друг, затова картата се показва само на него.
 */
export function PersonalDocumentsCard({ driverId }: { driverId: string }) {
  const files = useDocumentsOf({ driverId, types: TYPES });
  const [uploading, setUploading] = useState<Slot | null>(null);
  const [editing, setEditing] = useState<DocumentSummary | null>(null);
  const items = (files.data?.items ?? []).filter((d) => d.status !== 'superseded' && d.status !== 'rejected');

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-1.5">
            <Lock className="size-4 text-ink-dim" />
            Лични документи
          </span>
        }
        subtitle="Виждате ги само вие — диспечерът и счетоводителят нямат достъп до тях."
      />
      <CardBody>
        {files.isLoading ? (
          <LoadingBlock />
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {SLOTS.map((slot) => {
              const docs = items.filter((d) => d.type === slot.type);
              const latestUntil = docs.map((d) => d.validUntil).filter((v): v is string => Boolean(v)).sort().at(-1) ?? null;
              const days = latestUntil ? daysUntil(latestUntil) : null;
              return (
                <div
                  key={slot.type}
                  className={cn(
                    'rounded-lg border p-2.5',
                    docs.length === 0 ? 'border-dashed border-line-strong' : days !== null && days < 0 ? 'border-danger/50' : 'border-line',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{slot.label}</span>
                    {slot.expires && latestUntil ? (
                      <Badge tone={days !== null && days < 0 ? 'danger' : days !== null && days <= 30 ? 'warn' : 'ok'}>
                        {days !== null && days < 0 ? 'изтекъл' : `до ${formatDate(latestUntil)}`}
                      </Badge>
                    ) : null}
                    <Button size="sm" variant="ghost" className="ml-auto" icon={<Upload className="size-3.5" />} onClick={() => setUploading(slot)}>
                      {docs.length ? 'Още' : 'Качи'}
                    </Button>
                  </div>
                  {docs.length === 0 ? (
                    <p className="mt-1 text-[11px] text-ink-faint">{slot.hint}</p>
                  ) : (
                    <ul className="mt-1.5 space-y-1">
                      {docs.map((d) => (
                        <li key={d.id} className="flex items-center gap-2 text-xs">
                          {d.mimeType.startsWith('image/') && d.previewUrl ? (
                            <img src={d.previewUrl} alt="" className="size-9 shrink-0 rounded border border-line object-cover" />
                          ) : (
                            <FileText className="size-4 shrink-0 text-ink-dim" />
                          )}
                          <span className="min-w-0 flex-1 truncate text-ink-dim" title={d.fileName}>
                            {d.notes ? `${d.notes} · ` : ''}
                            {formatDate(d.createdAt)}
                            {d.validUntil ? ` · до ${formatDate(d.validUntil)}` : ''}
                          </span>
                          {d.previewUrl ? (
                            <a href={d.previewUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline" title="Отвори">
                              <Eye className="size-3.5" />
                            </a>
                          ) : null}
                          <IconButton label="Срок и бележка" onClick={() => setEditing(d)}>
                            <Pencil className="size-3.5" />
                          </IconButton>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
      {uploading ? <UploadDialog driverId={driverId} slot={uploading} onClose={() => setUploading(null)} /> : null}
      {editing ? <MetaDialog document={editing} onClose={() => setEditing(null)} /> : null}
    </Card>
  );
}

function addMonths(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function UploadDialog({ driverId, slot, onClose }: { driverId: string; slot: Slot; onClose: () => void }) {
  const upload = useUploadDocument();
  const [file, setFile] = useState<File | null>(null);
  const [validUntil, setValidUntil] = useState(slot.suggestMonths ? addMonths(slot.suggestMonths) : '');
  const [notes, setNotes] = useState('');

  const submit = () => {
    if (!file) return;
    upload.mutate(
      {
        file,
        target: {
          type: slot.type,
          driverId,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
          ...(slot.expires && validUntil ? { validUntil } : {}),
        },
      },
      {
        onSuccess: () => {
          toast.ok('Документът е качен', slot.label);
          onClose();
        },
        onError: (error) => toast.error('Не се качи', error instanceof Error ? error.message : undefined),
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Качване — ${slot.label}`}
      description={slot.hint}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button variant="primary" loading={upload.isPending} disabled={!file} onClick={submit}>
            Качи
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Файл (снимка или PDF)" required className="sm:col-span-2">
          <Input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </Field>
        {slot.expires ? (
          <Field label="Валиден до" hint={slot.suggestMonths ? 'Предложение — сверете с документа.' : undefined}>
            <DatePicker withTime={false} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </Field>
        ) : null}
        <Field label="Бележка" hint="например „лице“, „гръб“, „ADR основен курс“">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function MetaDialog({ document, onClose }: { document: DocumentSummary; onClose: () => void }) {
  const meta = useDocumentMeta();
  const [validUntil, setValidUntil] = useState(document.validUntil ?? '');
  const [notes, setNotes] = useState(document.notes ?? '');
  return (
    <Dialog
      open
      onClose={onClose}
      title="Срок и бележка"
      description={document.fileName}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button
            variant="primary"
            loading={meta.isPending}
            onClick={() =>
              meta.mutate(
                { id: document.id, validUntil: validUntil || null, notes: notes.trim() || null },
                {
                  onSuccess: () => {
                    toast.ok('Записано');
                    onClose();
                  },
                  onError: (error) => toast.error('Не е записано', error instanceof Error ? error.message : undefined),
                },
              )
            }
          >
            Запиши
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Валиден до">
          <DatePicker withTime={false} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </Field>
        <Field label="Бележка">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
